import React, { useState, useEffect, useCallback } from 'react';
import Dashboard from './components/Dashboard';
import CSVUpload from './components/DataEntry/CSVUpload';
import DataQuality from './components/DataQuality';
import DailyOverview from './components/DailyOverview';
import AnalyticsPage from './components/AnalyticsPage';
import BottomNav from './components/BottomNav';
import PullToRefreshIndicator from './components/PullToRefreshIndicator';
import usePullToRefresh from './hooks/usePullToRefresh';
import useSwipeNavigation from './hooks/useSwipeNavigation';

const VIEW_ORDER = [
  'dashboard',
  'csv-upload',
  'daily-overview',
  'analytics',
  'data-quality',
];

function App() {
  const [activeView, setActiveView] = useState('dashboard');
  const [refreshKey, setRefreshKey] = useState(0);
  const [darkMode, setDarkMode] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('darkMode');
    return saved === 'true';
  });

  useEffect(() => {
    // Apply dark mode class to html element
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    // Save preference
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  // Pull-to-Refresh
  const handleRefresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  const { pullDistance, isRefreshing } = usePullToRefresh(handleRefresh);

  // Swipe Navigation
  const { swipeOffset, isAnimating } = useSwipeNavigation(VIEW_ORDER, activeView, setActiveView);

  const renderView = () => {
    switch (activeView) {
      case 'dashboard':
        return <Dashboard key={refreshKey} />;
      case 'csv-upload':
        return <CSVUpload key={refreshKey} onBack={() => setActiveView('dashboard')} />;
      case 'data-quality':
        return <DataQuality key={refreshKey} />;
      case 'daily-overview':
        return <DailyOverview key={refreshKey} />;
      case 'analytics':
        return <AnalyticsPage key={refreshKey} />;
      default:
        return <Dashboard key={refreshKey} />;
    }
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="container">
          <h1>Health Tracker</h1>
          <nav className="nav">
            <button
              className={activeView === 'dashboard' ? 'active' : ''}
              onClick={() => setActiveView('dashboard')}
            >
              Dashboard
            </button>
            <button
              className={activeView === 'csv-upload' ? 'active' : ''}
              onClick={() => setActiveView('csv-upload')}
            >
              CSV Upload
            </button>
            <button
              className={activeView === 'daily-overview' ? 'active' : ''}
              onClick={() => setActiveView('daily-overview')}
            >
              Tagesübersicht
            </button>
            <button
              className={activeView === 'analytics' ? 'active' : ''}
              onClick={() => setActiveView('analytics')}
            >
              Analysen
            </button>
            <button
              className={activeView === 'data-quality' ? 'active' : ''}
              onClick={() => setActiveView('data-quality')}
            >
              Datenqualität
            </button>
            <button
              className="theme-toggle"
              onClick={() => setDarkMode(!darkMode)}
              title={darkMode ? 'Light Mode' : 'Dark Mode'}
            >
              {darkMode ? '\u2600\uFE0F' : '\uD83C\uDF19'}
            </button>
          </nav>
        </div>
      </header>

      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />

      <main className="app-main">
        <div className="container">
          <div
            className={`view-container ${isAnimating ? 'view-animating' : ''}`}
            style={swipeOffset ? { transform: `translateX(${swipeOffset}px)` } : undefined}
          >
            {renderView()}
          </div>
        </div>
      </main>

      <footer className="app-footer">
        <div className="container">
          <p>Health Tracker v1.0 - MVP</p>
        </div>
      </footer>

      <BottomNav activeView={activeView} onViewChange={setActiveView} />
    </div>
  );
}

export default App;
